import type { ChallengeLanguage, ChallengeTool } from "./types";

/**
 * Tool × language stacks a learner can choose. Every stack here has a graded AWS Lambda
 * runtime (targetqa-{tool}-{runtime}). Keep in sync with the backend allow-list in
 * backend/app/domain/preferences/stacks.py.
 */
export interface ToolDefinition {
  id: ChallengeTool;
  name: string;
  tagline: string;
  languages: ChallengeLanguage[];
  /** Test runner per language, shown on cards and in the workspace. */
  tooling: Record<string, string>;
}

export const TOOLS: Record<ChallengeTool, ToolDefinition> = {
  playwright: {
    id: "playwright",
    name: "Playwright",
    tagline: "Auto-waiting locators, web-first assertions and multi-page control.",
    languages: ["TypeScript", "JavaScript", "Python", "Java", "C#"],
    tooling: {
      TypeScript: "@playwright/test",
      JavaScript: "@playwright/test",
      Python: "Playwright + pytest",
      Java: "Playwright for Java + JUnit 5",
      "C#": "Playwright for .NET + NUnit",
    },
  },
  selenium: {
    id: "selenium",
    name: "Selenium",
    tagline: "WebDriver locators, explicit waits, Actions and window handling.",
    languages: ["Java", "Python", "C#"],
    tooling: {
      Java: "Selenium 4 + JUnit 5",
      Python: "Selenium 4 + pytest",
      "C#": "Selenium 4 + NUnit",
    },
  },
};

export const TOOL_ORDER: ChallengeTool[] = ["playwright", "selenium"];

export interface StackPreference {
  framework: ChallengeTool;
  language: ChallengeLanguage;
}

export const DEFAULT_STACK: StackPreference = { framework: "playwright", language: "TypeScript" };

export function isTool(value: unknown): value is ChallengeTool {
  return value === "playwright" || value === "selenium";
}

export function isValidStack(framework: unknown, language: unknown): boolean {
  return isTool(framework) && (TOOLS[framework].languages as string[]).includes(String(language));
}

/** Normalises any stored or remote value into a valid stack, or null. */
export function parseStack(value: unknown): StackPreference | null {
  if (!value || typeof value !== "object") return null;
  const { framework, language } = value as Record<string, unknown>;
  const tool = typeof framework === "string" ? framework.toLowerCase() : framework;
  return isValidStack(tool, language) ? { framework: tool as ChallengeTool, language: language as ChallengeLanguage } : null;
}

export function stackLabel(stack: StackPreference): string {
  return `${TOOLS[stack.framework].name} · ${stack.language}`;
}
